import { createSelector, Selector } from 'reselect'

import {
  AddressDataState,
  AddressState,
  AddressesOptions,
  AddressType,
  clientSearchResponseAddressInfo,
} from '../models/address.model'
import { addressInitialState } from '../store/address.reducer'

import { filterFalsyValues } from '~/core/helpers/helpers'
import { RootState } from '~/core/store/root.reducer'
import { selectClientSearch } from '~/customer/store/customer.selectors'
import { selectPaymentAddress } from '~/payments/store/payments.selectors'
import { LineTypes } from '~/tariff/model/tariff.model'

type GetAddress = Selector<RootState, AddressState>

const getAddress: GetAddress = (state) => state.address

const getId = (_: RootState, id: string) => id

const getType = (_: RootState, _id: string, type: AddressType) => type

export const selectAddress = createSelector(
  getAddress,
  getId,
  (address, id) => address && address[id],
)

export const selectAllSimAddresses = createSelector(getAddress, (addressState) =>
  Object.entries(addressState)
    .map((lineAddress) => lineAddress[1].SIM.data.summary)
    .filter((i) => !!i),
)

export const selectAddressByType = createSelector(
  getAddress,
  getId,
  getType,
  (address, id, addressType): AddressDataState => {
    const initAddress =
      address && address[id] && address[id][addressType]
        ? address[id][addressType]
        : addressInitialState
    return id !== LineTypes.MAIN && initAddress.addressOwner === AddressesOptions.SAME
      ? address.mainLine[addressType]
      : initAddress
  },
)

export const selectAddressOwner = createSelector(
  getAddress,
  getId,
  getType,
  (address, id, addressType) =>
    address && address[id] ? address[id][addressType].addressOwner : null,
)

export const selectAddressSummary = createSelector(
  selectAddressByType,
  ({
    data: {
      streetName,
      streetType,
      gescal,
      number,
      floor,
      letter,
      town,
      postalCode,
      province,
      summary,
    },
  }) => {
    const street = gescal ? `${streetType} ${streetName}` : summary
    return `${street} ${number} ${floor} ${letter}, ${town} ${postalCode} ${province} `
  },
)

export const getSummaryAddressFromClientSearch = (
  clientSearchAddress: clientSearchResponseAddressInfo | undefined,
) => {
  if (clientSearchAddress) {
    const { streetName, town, postalCode, province } = clientSearchAddress
    return `${streetName}, ${town} ${postalCode?.toString()} ${province} `
  }
  return ''
}

export const selectAddressFromClientSearch = createSelector(selectClientSearch, (clientSearch) => {
  const { data } = clientSearch

  const clientSearchType = data?.customer ? 'customer' : 'enterprise'

  const customerAddress =
    data?.customer &&
    filterFalsyValues({
      ...data?.enterprise?.address?.billing,
      provinceId: data?.customer?.address?.billing?.provinceId.toString(),
      postalCode: data?.customer?.address?.billing?.postalCode?.toString(),
      summary: getSummaryAddressFromClientSearch(
        data?.customer?.address?.billing ? data?.customer?.address?.billing : undefined,
      ),
    })

  const enterpriseAddress =
    data?.enterprise &&
    filterFalsyValues({
      ...data?.enterprise?.address?.billing,
      addressInput: '',
      provinceId: data?.enterprise?.address?.billing?.provinceId.toString(),
      postalCode: data?.enterprise?.address?.billing?.postalCode?.toString(),
      summary: getSummaryAddressFromClientSearch(
        data?.enterprise?.address?.billing ? data?.enterprise?.address?.billing : undefined,
      ),
    })

  const clientSearchAddresses = filterFalsyValues({
    customer: customerAddress,
    enterprise: enterpriseAddress,
  })

  return clientSearchAddresses[clientSearchType as keyof typeof clientSearchAddresses]
})

export const selectBillingAddress = createSelector(
  selectPaymentAddress,
  getAddress,
  (paymentAddress, address) => {
    return paymentAddress === 'OTHER_ADDRESS'
      ? address.all[AddressType.BILLING].data.summary
      : paymentAddress
  },
)

export default getAddress
